// Helper para construir URLs de assets del backend
// Las rutas guardadas en la BD son relativas (ej: /uploads/logos/logo-xxx.png)
// pero el frontend corre en otro origen, así que necesitamos agregar el host del backend
// Además, los uploads están protegidos con JWT, así que agregamos ?token=<access_token>

const BACKEND_URL = import.meta.env.VITE_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000'

let _accessToken: string | null = null

export function setAccessToken(token: string | null) {
  _accessToken = token
}

export function getAccessToken(): string | null {
  return _accessToken
}

/** Obtiene el token: primero de memoria, luego de localStorage como fallback */
function _getToken(): string | null {
  if (_accessToken) return _accessToken
  try { return localStorage.getItem('auth_token') } catch { return null }
}

/**
 * Construye la URL pública de un asset del backend.
 * - Si el path es absoluto (http://, https://, data:), lo retorna tal cual
 *   PERO si apunta a nuestro backend en /uploads/ y hay token, le agrega ?token=
 *   (necesario porque <img> no puede mandar headers de Authorization).
 * - Si es relativo, le agrega el host del backend.
 * - Si hay un access_token seteado, le agrega ?token=<token> para que el
 *   UploadsController del backend autorice la descarga.
 */
export function getAssetUrl(path: string | null | undefined): string {
  if (!path) return ''

  const token = _getToken()

  // URLs absolutas externas (CDN, data:, etc.) — retornar tal cual
  if (path.startsWith('data:')) return path
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Si apunta a nuestro backend en /uploads/, hay que agregar ?token
    if (token && path.includes('/uploads/')) {
      const sep = path.includes('?') ? '&' : '?'
      // Evitar duplicar el token
      if (path.includes('token=')) return path
      return `${path}${sep}token=${encodeURIComponent(token)}`
    }
    return path
  }

  // Ruta relativa: agregarle el host del backend
  const base = `${BACKEND_URL}${path.startsWith('/') ? path : '/' + path}`
  // Si es una URL de uploads y tenemos token, agregarlo
  if (token && base.includes('/uploads/')) {
    const sep = base.includes('?') ? '&' : '?'
    if (base.includes('token=')) return base
    return `${base}${sep}token=${encodeURIComponent(token)}`
  }
  return base
}

export function getBackendUrl(): string {
  return BACKEND_URL
}

/**
 * Procesa el HTML de la firma de Gmail para visualización.
 *
 * Estrategia:
 * - Las imágenes que el backend pudo descargar vienen EMBEBIDAS como data URIs
 *   (base64) — se dejan tal cual y siempre funcionan (local y producción).
 * - Las imágenes PRIVADAS de Google Drive (que el backend no puede descargar
 *   porque requieren sesión de Google) se dejan con su URL directa de Google
 *   (ci3/lh3.googleusercontent.com). El navegador del usuario las carga con
 *   sus propias cookies de Google (el usuario está logueado en Gmail).
 *
 * IMPORTANTE: NO proxear las URLs de Google a través del backend — el servidor
 * no tiene las cookies de Google del usuario y recibiría una página de login
 * HTML en lugar de la imagen (eso causaba la imagen rota).
 */
export function rewriteSignatureImages(html: string): string {
  if (!html) return ''
  // Dejar todo tal cual: data URIs embebidas funcionan siempre,
  // y las URLs directas de Google las resuelve el navegador con las cookies del usuario.
  return html
}

/**
 * Sanitiza un htmlBody (de email) para visualización segura.
 * - Agrega tokens a URLs de /uploads/*
 * - Reemplaza cid: references (imágenes inline perdidas)
 * - Oculta tracking pixels (1×1, 0×0)
 * - Convierte URLs relativas de imágenes a placeholder
 */
export function sanitizeHtmlBody(html: string | null | undefined): string {
  if (!html) return ''
  const token = _getToken()

  let result = html

  // 1. Reemplazar cid: references — no se pueden cargar en contexto web
  result = result.replace(
    /<img\s+([^>]*?)src=(["'])cid:[^"' ]*\2([^>]*)>/gi,
    '<!-- [imagen inline no disponible] -->'
  )

  // 2. Ocultar tracking pixels (imágenes de 1×1, 0×0, o con dimensiones ocultas)
  result = result.replace(
    /<img\s+([^>]*?)>/gi,
    (match, attrs) => {
      const lowAttrs = attrs.toLowerCase()
      // Detectar tracking pixels: width=0|1, height=0|1, o style con display:none/visibility:hidden
      const isTracking = /\b(width|height)\s*=\s*["']?[01]["']?/i.test(attrs) ||
        /display\s*:\s*none/i.test(attrs) ||
        /visibility\s*:\s*hidden/i.test(attrs)
      if (isTracking) return '<!-- [tracking pixel oculto] -->'
      return match
    }
  )

  // 3. Reemplazar URLs relativas de imágenes (no-http, no-data, no-cid, no-/uploads/) con placeholder
  result = result.replace(
    /<img\s+([^>]*?)src=(["'])(?!https?:\/\/|data:|cid:|\/uploads\/)([^"' ]+)\2/gi,
    (match, before, q, path) => {
      // No tocar si ya fue reemplazado por un comentario
      if (match.startsWith('<!--')) return match
      return `<img ${before}src=${q}${q} alt="" style="display:none"${match.endsWith('/>') ? '/>' : '>'}`
    }
  )

  // 4. Agregar token a URLs de /uploads/
  if (token) {
    result = result
      .replace(/(src|href)=(["'])(https?:\/\/[^"' ]*\/uploads\/[^"' ]*)\2/gi, (_m, attr, q, url) => {
        const sep = url.includes('?') ? '&' : '?'
        return `${attr}=${q}${url}${sep}token=${encodeURIComponent(token)}${q}`
      })
      .replace(/(src|href)=(["'])(\/uploads\/[^"' ]*)\2/gi, (_m, attr, q, url) => {
        const sep = url.includes('?') ? '&' : '?'
        return `${attr}=${q}${BACKEND_URL}${url}${sep}token=${encodeURIComponent(token)}${q}`
      })
  }

  return result
}
